import { createRoot, type Root } from "react-dom/client";
import "./styles.css";
import { OrbitProvider } from "./provider";
import { OrbitClientPanel, type OrbitClientPanelProps } from "./widgets/OrbitClientPanel";

type HostedPanelMode = NonNullable<OrbitClientPanelProps["mode"]>;
type HostedPanelTab = NonNullable<OrbitClientPanelProps["defaultTab"]>;
type HostedLauncherPosition = NonNullable<OrbitClientPanelProps["launcherPosition"]>;
type HostedLauncherVariant = NonNullable<OrbitClientPanelProps["launcherVariant"]>;

interface OrbitBridgeWidgetConfig {
  mountId?: string;
  mountSelector?: string;
  endpoint: string;
  anonKey: string;
  tokenEndpoint?: string;
  theme?: "inherit" | "light" | "dark" | "orbit";
  mode?: HostedPanelMode;
  defaultTab?: HostedPanelTab;
  launcherLabel?: string;
  launcherPosition?: HostedLauncherPosition;
  launcherVariant?: HostedLauncherVariant;
  title?: string;
  subtitle?: string;
  connectTitle?: string;
  connectDescription?: string;
  connectLabel?: string;
  assetBaseUrl?: string;
}

interface OrbitBridgeWidgetApi {
  mount(config: OrbitBridgeWidgetConfig): () => void;
  unmount(mountId?: string): void;
}

interface MountedWidget {
  container: HTMLElement;
  root: Root;
}

declare global {
  interface Window {
    OrbitBridgeWidget?: OrbitBridgeWidgetApi;
    OrbitBridgeWidgetQueue?: OrbitBridgeWidgetConfig[];
  }
}

const DEFAULT_MOUNT_ID = "orbit-bridge-widget-root";
const STYLE_LINK_ID = "orbit-bridge-widget-styles";
const STYLE_VERSION = "20260724-bridge-flash-guard-v1";
const mountedWidgets = new Map<string, MountedWidget>();
let stylesheetReady: Promise<void> | null = null;

const getCurrentScript = (): HTMLScriptElement | null => {
  const script = document.currentScript;
  if (script instanceof HTMLScriptElement) return script;

  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src*='/bridge/widget.js']"));
  return scripts[scripts.length - 1] ?? null;
};

const getScriptBaseUrl = (): string => {
  const script = getCurrentScript();
  if (!script?.src) return "https://myorbit.dev/bridge/";
  return new URL(".", script.src).toString();
};

const injectStylesheet = (assetBaseUrl?: string): Promise<void> => {
  const href = new URL(`widget.css?v=${STYLE_VERSION}`, assetBaseUrl || getScriptBaseUrl()).toString();
  const existing = document.getElementById(STYLE_LINK_ID);

  if (existing instanceof HTMLLinkElement) {
    if (existing.dataset.orbitVersion !== STYLE_VERSION || existing.href !== href) {
      existing.href = href;
      existing.dataset.orbitVersion = STYLE_VERSION;
    }
    if (existing.sheet || existing.dataset.orbitLoaded === "true") {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      existing.addEventListener(
        "load",
        () => {
          existing.dataset.orbitLoaded = "true";
          resolve();
        },
        { once: true },
      );
      existing.addEventListener("error", () => resolve(), { once: true });
      window.setTimeout(() => resolve(), 200);
    });
  }

  if (stylesheetReady) return stylesheetReady;

  stylesheetReady = new Promise((resolve) => {
    const link = document.createElement("link");
    link.id = STYLE_LINK_ID;
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.orbitVersion = STYLE_VERSION;
    link.dataset.orbitLoaded = "false";
    link.addEventListener(
      "load",
      () => {
        link.dataset.orbitLoaded = "true";
        resolve();
      },
      { once: true },
    );
    link.addEventListener("error", () => resolve(), { once: true });
    document.head.appendChild(link);
    window.setTimeout(() => resolve(), 800);
  });

  return stylesheetReady;
};

const readSupabaseAccessToken = (): string | null => {
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;

      const value = window.localStorage.getItem(key);
      if (!value) continue;

      const parsed = JSON.parse(value) as {
        access_token?: unknown;
        currentSession?: { access_token?: unknown };
      };
      const token = parsed.access_token || parsed.currentSession?.access_token;
      if (typeof token === "string" && token.trim()) return token;
    }
  } catch {
    return null;
  }

  return null;
};

const readTokenResponse = async (response: Response): Promise<string> => {
  if (!response.ok) {
    throw new Error(`Orbit Bridge token failed (${response.status})`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = (await response.json()) as { token?: unknown };
    if (typeof data.token === "string" && data.token.trim()) return data.token;
    throw new Error("Orbit Bridge token response did not include a token");
  }

  const token = await response.text();
  if (!token.trim()) throw new Error("Orbit Bridge token response was empty");
  return token.trim();
};

const createTokenReader = (tokenEndpoint?: string): (() => Promise<string>) => {
  const endpoint = tokenEndpoint?.trim() || "/api/orbit-bridge-token";

  return async () => {
    const accessToken = readSupabaseAccessToken();
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
    const response = await fetch(endpoint, {
      credentials: endpoint.startsWith("/") ? "include" : "same-origin",
      headers,
    });

    return readTokenResponse(response);
  };
};

const ensureMountContainer = (config: OrbitBridgeWidgetConfig): { id: string; element: HTMLElement } => {
  const id = config.mountId?.trim() || DEFAULT_MOUNT_ID;

  if (config.mountSelector) {
    const selected = document.querySelector<HTMLElement>(config.mountSelector);
    if (selected) return { id, element: selected };
    // Refuse body fallback when an inline selector was requested — mounting to
    // document.body briefly paints a fixed-corner launcher (flash).
    console.error(
      `[Orbit Bridge] mountSelector "${config.mountSelector}" not found; refusing body fallback.`,
    );
    const orphan = document.createElement("div");
    orphan.hidden = true;
    orphan.setAttribute("data-orbit-bridge-orphan", "true");
    return { id, element: orphan };
  }

  const existing = document.getElementById(id);
  if (existing) return { id, element: existing };

  const container = document.createElement("div");
  container.id = id;
  container.setAttribute("data-orbit-bridge-host", "true");
  document.body.appendChild(container);
  return { id, element: container };
};

const mount = (config: OrbitBridgeWidgetConfig): (() => void) => {
  if (!config.endpoint?.trim() || !config.anonKey?.trim()) {
    console.error("[Orbit Bridge] endpoint and anonKey are required for the hosted widget.");
    return () => undefined;
  }

  let cancelled = false;
  let mountedId: string | null = null;

  void injectStylesheet(config.assetBaseUrl).then(() => {
    if (cancelled) return;

    const { id, element } = ensureMountContainer(config);
    mountedId = id;
    mountedWidgets.get(id)?.root.unmount();

    const root = createRoot(element);
    mountedWidgets.set(id, { container: element, root });
    root.render(
      <OrbitProvider
        endpoint={config.endpoint}
        anonKey={config.anonKey}
        token={createTokenReader(config.tokenEndpoint)}
        theme={config.theme || "inherit"}
      >
        <OrbitClientPanel
          mode={config.mode || "floating"}
          defaultTab={config.defaultTab || "inbox"}
          launcherLabel={config.launcherLabel || "Orbit"}
          launcherPosition={config.launcherPosition || "bottom-left"}
          launcherVariant={config.launcherVariant || "icon"}
          title={config.title || "Orbit"}
          subtitle={config.subtitle || ""}
          connectTitle={config.connectTitle || "Connect Orbit"}
          connectDescription={
            config.connectDescription ||
            "Use this from your signed-in dashboard to connect messages, work updates, requests, files, recordings, and billing notices."
          }
          connectLabel={config.connectLabel || "Connect Orbit"}
        />
      </OrbitProvider>,
    );
  });

  return () => {
    cancelled = true;
    if (mountedId) unmount(mountedId);
  };
};

const unmount = (mountId = DEFAULT_MOUNT_ID): void => {
  const mounted = mountedWidgets.get(mountId);
  if (!mounted) return;

  mounted.root.unmount();
  if (mounted.container.getAttribute("data-orbit-bridge-host") === "true") {
    mounted.container.remove();
  }
  mountedWidgets.delete(mountId);
};

const readScriptConfig = (): OrbitBridgeWidgetConfig | null => {
  const script = getCurrentScript();
  if (!script) return null;

  const endpoint = script.dataset.endpoint || script.dataset.orbitEndpoint || "";
  const anonKey = script.dataset.anonKey || script.dataset.orbitAnonKey || "";
  if (!endpoint || !anonKey) return null;

  return {
    mountId: script.dataset.mountId,
    mountSelector: script.dataset.mountSelector,
    endpoint,
    anonKey,
    tokenEndpoint: script.dataset.tokenEndpoint || script.dataset.orbitTokenEndpoint,
    theme: script.dataset.theme as OrbitBridgeWidgetConfig["theme"],
    mode: script.dataset.mode as HostedPanelMode | undefined,
    defaultTab: script.dataset.defaultTab as HostedPanelTab | undefined,
    launcherLabel: script.dataset.launcherLabel,
    launcherPosition: script.dataset.launcherPosition as HostedLauncherPosition | undefined,
    launcherVariant: script.dataset.launcherVariant as HostedLauncherVariant | undefined,
    title: script.dataset.title,
    subtitle: script.dataset.subtitle,
    connectTitle: script.dataset.connectTitle,
    connectDescription: script.dataset.connectDescription,
    connectLabel: script.dataset.connectLabel,
    assetBaseUrl: script.dataset.assetBaseUrl,
  };
};

const api: OrbitBridgeWidgetApi = { mount, unmount };
const queuedConfigs = window.OrbitBridgeWidgetQueue ?? [];
window.OrbitBridgeWidget = api;
window.OrbitBridgeWidgetQueue = [];

for (const config of queuedConfigs) {
  mount(config);
}

const scriptConfig = readScriptConfig();
if (scriptConfig) {
  mount(scriptConfig);
}
